import { forwardRef, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { groupBy } from 'lodash';
import { tv } from 'tailwind-variants';

import { LoadError } from '@/components/ui/load-error';
import { Loading } from '@/components/ui/old/Loading';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useConfetti } from '@/providers/confetti';

import { CreateAlertTask } from '../api/mutations/create-alert-task';
import { CreateStandardTask } from '../api/mutations/create-standard-task';
import { UpdateTask } from '../api/mutations/update-task';
import { ListTasks } from '../api/queries/list-tasks';
import { TaskCardType } from '../entities/task-card-type';
import type { TaskPreview } from '../entities/task-preview';
import { TaskStatus } from '../entities/task-status';
import { useFilterConfig } from '../hooks/use-filter-config';

import { AlertSuggestionsColumn } from './alert-suggestions-column';
import { AlertFeedbackDialog } from './task-details/actions/alert-feedback-dialog';
import { RequestResetDialog } from './task-details/actions/request-reset-dialog';
import { TasksColumn } from './tasks-column';

export type TasksBoardProps = {
  filters: ListTasks.Filters;
  className?: string;
  onTaskOpen: (taskId: string) => void;
};

const TasksBoard = forwardRef<HTMLDivElement, TasksBoardProps>(function (
  { filters, onTaskOpen, className },
  ref,
) {
  const history = useHistory();
  const featureFlag = useFeatureFlag();
  const confetti = useConfetti();
  const updateTask = UpdateTask.useMutation();
  const createStandardTask = CreateStandardTask.useMutation();
  const createAlertTask = CreateAlertTask.useMutation();
  const listTasks = ListTasks.useQuery(
    {
      filters,
      includeAlerts: featureFlag('alerts'),
    },
    {
      refetchInterval: 30_000,
      keepPreviousData: true,
    },
  );
  const [feedbackTask, setFeedbackTask] = useState<TaskPreview | undefined>(
    undefined,
  );
  const [resetScoreTask, setResetScoreTask] = useState<TaskPreview | undefined>(
    undefined,
  );
  const { shouldHideCardType } = useFilterConfig(filters);

  const tasksPerStatus = useMemo(
    () =>
      groupBy(listTasks.data?.tasks ?? [], (task) => task.status) as Record<
        TaskStatus,
        TaskPreview[] | undefined
      >,
    [listTasks.data],
  );

  const filteredSuggestions = useMemo(() => {
    if (shouldHideCardType(TaskCardType.ALERT)) {
      return [];
    }

    return listTasks.data?.alertSuggestions ?? [];
  }, [listTasks.data?.alertSuggestions, shouldHideCardType]);

  const style = tasksBoardStyle();

  if (listTasks.isLoading) {
    return <Loading />;
  }

  if (listTasks.isError) {
    return <LoadError title="An error occurred while loading the task board" />;
  }

  return (
    <div
      ref={ref}
      data-testid="tasks-board"
      className={style.container({ className })}
    >
      {featureFlag('alerts') ? (
        <div className={style.alertsContainer()}>
          <AlertSuggestionsColumn
            alertSuggestions={filteredSuggestions}
            draggable
            onAlertTaskCreate={async function (input) {
              const task = await createAlertTask.mutateAsync(input);
              history.replace({
                search: new URLSearchParams({ id: task.id }).toString(),
              });
            }}
          />
        </div>
      ) : null}
      <div className={style.tasksContainer()}>
        {[
          TaskStatus.OPEN,
          TaskStatus.IN_PROGRESS,
          TaskStatus.EXTERNAL_REVIEW,
          TaskStatus.DONE,
        ].map((status) => (
          <TasksColumn
            key={status}
            draggable
            status={status}
            tasks={tasksPerStatus[status] ?? []}
            onTaskOpen={onTaskOpen}
            onTaskUpdate={async function (
              input: UpdateTask.Input,
              task: TaskPreview,
            ) {
              try {
                if (input.status) {
                  // Call setFeedbackTask even before the end of updateTask.mutateAsync, if it fails we call setResetScoreTask with undefined
                  setFeedbackTask({ ...task, status: input.status });
                }
                await updateTask.mutateAsync(input);
              } catch (_) {
                setFeedbackTask(undefined);
              }
            }}
            onStandardTaskCreate={
              status !== TaskStatus.DONE
                ? async function (input) {
                    const task = await createStandardTask.mutateAsync(input);
                    history.replace({
                      search: new URLSearchParams({ id: task.id }).toString(),
                    });
                  }
                : undefined
            }
            onAlertTaskCreate={async function (input) {
              const task = await createAlertTask.mutateAsync(input);
              history.replace({
                search: new URLSearchParams({ id: task.id }).toString(),
              });
            }}
          />
        ))}
      </div>
      {feedbackTask ? (
        <AlertFeedbackDialog
          task={feedbackTask}
          newStatus={feedbackTask.status}
          open={Boolean(feedbackTask)}
          onClose={function () {
            setResetScoreTask(feedbackTask);
            setFeedbackTask(undefined);
          }}
        />
      ) : null}
      {resetScoreTask ? (
        <RequestResetDialog
          cancelText="Skip for now"
          task={resetScoreTask}
          open={Boolean(resetScoreTask)}
          onClose={function () {
            setResetScoreTask(undefined);
            if (featureFlag('taskmanagerConfetti')) {
              confetti({});
            }
          }}
        />
      ) : null}
    </div>
  );
});

const tasksBoardStyle = tv({
  slots: {
    container: `
      flex
      size-full
      flex-row
      overflow-y-hidden
    `,
    alertsContainer: `
      w-96
      flex-shrink-0
      border-r
      px-4
      pt-4
    `,
    tasksContainer: `
      grid
      flex-grow
      grid-cols-[repeat(4,_minmax(theme(spacing.72),_theme(spacing.80)))]
      gap-8
      px-4
      pt-4
    `,
  },
});

TasksBoard.displayName = 'TasksBoard';
export { TasksBoard };
